import type { Store } from '@reduxjs/toolkit';
import getOr from 'lodash/fp/getOr';

import { trace } from '../../configuration/setup/trace';
import { DEFAULT_LOCALE, getSupportedLocale as defaultGetSupportedLocale } from './lang';
import { displayMessagesFetched, localeChanged } from './langSlice';

const normalizeDynamicImport = (imported: unknown) => getOr(imported, 'default', imported);

const localeImportMap: Record<string, () => Promise<unknown>> = {
    'bg-BG': () => import('../../configuration/translations/bg-BG.json'),
    'cs-CZ': () => import('../../configuration/translations/cs-CZ.json'),
    'da-DK': () => import('../../configuration/translations/da-DK.json'),
    'de-DE': () => import('../../configuration/translations/de-DE.json'),
    'el-GR': () => import('../../configuration/translations/el-GR.json'),
    'en-GB': () => import('../../configuration/translations/en-GB.json'),
    'es-ES': () => import('../../configuration/translations/es-ES.json'),
    'et-EE': () => import('../../configuration/translations/et-EE.json'),
    'fi-FI': () => import('../../configuration/translations/fi-FI.json'),
    'fr-FR': () => import('../../configuration/translations/fr-FR.json'),
    'hr-HR': () => import('../../configuration/translations/hr-HR.json'),
    'hu-HU': () => import('../../configuration/translations/hu-HU.json'),
    'it-IT': () => import('../../configuration/translations/it-IT.json'),
    'ko-KR': () => import('../../configuration/translations/ko-KR.json'),
    'lt-LT': () => import('../../configuration/translations/lt-LT.json'),
    'lv-LV': () => import('../../configuration/translations/lv-LV.json'),
    'nb-NO': () => import('../../configuration/translations/nb-NO.json'),
    'nl-NL': () => import('../../configuration/translations/nl-NL.json'),
    'pl-PL': () => import('../../configuration/translations/pl-PL.json'),
    'pt-BR': () => import('../../configuration/translations/pt-BR.json'),
    'pt-PT': () => import('../../configuration/translations/pt-PT.json'),
    'ro-RO': () => import('../../configuration/translations/ro-RO.json'),
    'sk-SK': () => import('../../configuration/translations/sk-SK.json'),
    'sl-SI': () => import('../../configuration/translations/sl-SI.json'),
    'sv-SE': () => import('../../configuration/translations/sv-SE.json'),
};

export const importDisplayMessages = (locale: string) => {
    const importFn = localeImportMap[locale];
    if (!importFn) {
        return Promise.reject(new Error(`Invalid locale: ${locale}`));
    }
    return importFn()
        .then(normalizeDynamicImport)
        .catch(error => {
            trace.error(`Could not load translation file for locale: ${locale}`, error);
            throw error;
        });
};

export const configureFetchDisplayMessages =
    (store: Store, fetchDisplayMessages = importDisplayMessages, getSupportedLocale = defaultGetSupportedLocale) =>
    async (locale: string) => {
        if (!locale) {
            const warnMessage = 'No "locale" supplied when fetching display messages!';
            trace.warn(warnMessage);
            return Promise.reject(new Error(warnMessage));
        }

        store.dispatch(localeChanged(locale));
        const supportedLocale = getSupportedLocale(locale);

        try {
            const displayMessages = await fetchDisplayMessages(supportedLocale);
            store.dispatch(displayMessagesFetched({ locale: supportedLocale, displayMessages }));
        } catch (error: unknown) {
            trace.error(`Display messages for "${supportedLocale}" could not be fetched.`, error);
            store.dispatch(localeChanged(DEFAULT_LOCALE));
        }
    };
